import type { AlertColor } from '@mui/material';
import { Alert } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useMemo } from 'react';
import type { SnackbarMessage } from '../redux/trip/trip.types';

export type ToastApi = {
  toast: (text: string, severity?: AlertColor) => void;
  success: (text: string) => void;
  error: (text: string) => void;
  info: (text: string) => void;
  warning: (text: string) => void;
  /**
   * Accepts the legacy `SnackbarMessage` object (or `null`, which is a no-op).
   *
   * A migration shim: components that took a `setSnackbarMessage` prop can call
   * this instead and drop the prop, which makes TypeScript point at every parent
   * still passing it. Delete once no `setSnackbarMessage` props remain.
   */
  showSnackbarMessage: (message: SnackbarMessage | null) => void;
};

/**
 * The app's toast API, backed by the notistack provider in `index.tsx`.
 *
 * Consolidates the local `components/Snackbar/Snackbar.tsx` component — which
 * required every caller to own `snackbarMessage` state and thread a
 * `setSnackbarMessage` callback down through its children — into a hook that
 * needs no state at all.
 *
 * Position and duration are set per call rather than on the provider, so these
 * keep the old component's top-center / 1500ms presentation while the two
 * pre-existing notistack call sites keep their bottom-left undo toasts.
 */
export function useToast(): ToastApi {
  const { enqueueSnackbar } = useSnackbar();

  return useMemo(() => {
    const toast = (text: string, severity: AlertColor = 'info') => {
      enqueueSnackbar(text, {
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
        autoHideDuration: 1500,
        // Rendered as an MUI Alert so it matches what the old component showed.
        content: (key) =>
          React.createElement('div', { key }, React.createElement(Alert, { severity }, text)),
      });
    };

    return {
      toast,
      success: (text: string) => toast(text, 'success'),
      error: (text: string) => toast(text, 'error'),
      info: (text: string) => toast(text, 'info'),
      warning: (text: string) => toast(text, 'warning'),
      showSnackbarMessage: (message: SnackbarMessage | null) => {
        if (message) {
          toast(message.text, message.severity);
        }
      },
    };
  }, [enqueueSnackbar]);
}

export default useToast;
